import { FC, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_DROPBOX_ROOT,
  DROPBOX_ROOT_OPTIONS,
} from 'dashboard409a/components/OperationsModal/dropbox.constants';
import { getDropboxFolderDisplayName } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import { listWorkboardDirectories, listWorkboardFiles } from 'dashboard409a/services/dashboard';
import FileCheckboxList from '../FileCheckboxList';
import { FileCheckboxItem } from '../ProcessReportModal.types';
import { toggleExclusivePath } from '../ProcessReportModal.utils';
import { WorkboardDropboxPickerProps } from './WorkboardDropboxPicker.props';

const WorkboardDropboxPicker: FC<WorkboardDropboxPickerProps> = ({
  companyName,
  initialPrefix = '',
  initialDropboxRoot = DEFAULT_DROPBOX_ROOT,
  selectedModelKey,
  selectedReportKey,
  onSelectModel,
  onSelectReport,
}) => {
  // Path relative to the selected valuation root that the analyst has navigated into.
  const [prefix, setPrefix] = useState(initialPrefix);
  const [dropboxRoot, setDropboxRoot] = useState(initialDropboxRoot);
  const [folderFilter, setFolderFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  const segments = useMemo(() => prefix.split('/').filter(Boolean), [prefix]);
  const atRoot = segments.length === 0;

  useEffect(() => {
    setPrefix(initialPrefix);
  }, [initialPrefix]);

  useEffect(() => {
    setDropboxRoot(initialDropboxRoot);
  }, [initialDropboxRoot]);

  // Seed the company search with the deal's company name when starting at the root.
  useEffect(() => {
    if (atRoot && companyName) {
      setFolderFilter(companyName);
    }
  }, [atRoot, companyName]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(folderFilter.trim()), 350);
    return () => clearTimeout(timer);
  }, [folderFilter]);

  const { data: directories = [], isFetching: isLoadingDirs } = useQuery({
    queryKey: ['workboard-dirs', dropboxRoot, prefix, debouncedFilter],
    queryFn: () => listWorkboardDirectories({ prefix, q: debouncedFilter, root: dropboxRoot }),
  });

  // Once the current folder contains Model/Report subfolders (the valuation
  // folder), lock navigation and source files from those respective directories.
  const modelDir = useMemo(
    () => directories.find(directory => directory.name.toLowerCase().includes('model')),
    [directories]
  );
  const reportDir = useMemo(
    () => directories.find(directory => directory.name.toLowerCase().includes('report')),
    [directories]
  );
  const lockedIn = !atRoot && !isLoadingDirs && (Boolean(modelDir) || Boolean(reportDir) || directories.length === 0);
  const modelPrefix = modelDir?.path ?? prefix;
  const reportPrefix = reportDir?.path ?? prefix;

  const { data: modelFiles = [], isFetching: isLoadingModel } = useQuery({
    queryKey: ['workboard-files', dropboxRoot, modelPrefix, 'model'],
    queryFn: () => listWorkboardFiles({ prefix: modelPrefix, kind: 'model', root: dropboxRoot }),
    enabled: lockedIn,
  });

  const { data: reportFiles = [], isFetching: isLoadingReport } = useQuery({
    queryKey: ['workboard-files', dropboxRoot, reportPrefix, 'report'],
    queryFn: () => listWorkboardFiles({ prefix: reportPrefix, kind: 'report', root: dropboxRoot }),
    enabled: lockedIn,
  });

  const navLabel = atRoot ? 'Search company folder' : 'Select valuation folder';

  // Show folders newest-first below the company root (e.g. most recent valuation
  // folder first), and reverse the file lists similarly.
  const displayedDirectories = useMemo(
    () => (atRoot ? directories : [...directories].reverse()),
    [directories, atRoot]
  );
  const displayedModelFiles = useMemo<FileCheckboxItem[]>(
    () => [...modelFiles].reverse().map(file => ({ id: file.key, name: file.name, path: file.key })),
    [modelFiles]
  );
  const displayedReportFiles = useMemo<FileCheckboxItem[]>(
    () => [...reportFiles].reverse().map(file => ({ id: file.key, name: file.name, path: file.key })),
    [reportFiles]
  );

  const navigateToSegment = (index: number) => {
    setFolderFilter('');
    if (index < 0) {
      setPrefix('');
      return;
    }
    setPrefix(`${segments.slice(0, index + 1).join('/')}/`);
  };

  const enterFolder = (folderPath: string) => {
    setFolderFilter('');
    setPrefix(folderPath.endsWith('/') ? folderPath : `${folderPath}/`);
  };

  const changeDropboxRoot = (nextRoot: string) => {
    setFolderFilter('');
    setPrefix('');
    setDropboxRoot(nextRoot);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
        select
        label="Storage Root"
        value={dropboxRoot}
        onChange={event => changeDropboxRoot(event.target.value)}
        size="small"
        fullWidth
      >
        {DROPBOX_ROOT_OPTIONS.map(root => (
          <MenuItem key={root} value={root}>
            {root}
          </MenuItem>
        ))}
      </TextField>

      <Breadcrumbs maxItems={4} sx={{ '& .MuiBreadcrumbs-li': { maxWidth: 160 } }}>
        <Button
          variant="text"
          size="small"
          onClick={() => navigateToSegment(-1)}
          sx={{ minWidth: 0, p: 0, textTransform: 'none', fontWeight: 'normal' }}
        >
          {getDropboxFolderDisplayName(dropboxRoot)}
        </Button>
        {segments.map((segment, index) =>
          index < segments.length - 1 ? (
            <Button
              key={segments.slice(0, index + 1).join('/')}
              variant="text"
              size="small"
              onClick={() => navigateToSegment(index)}
              sx={{
                minWidth: 0,
                p: 0,
                textTransform: 'none',
                fontWeight: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {segment}
            </Button>
          ) : (
            <Typography key={segments.slice(0, index + 1).join('/')} variant="body2" color="text.primary" noWrap>
              {segment}
            </Typography>
          )
        )}
      </Breadcrumbs>

      {!lockedIn && (
        <Autocomplete
          size="small"
          value={null}
          inputValue={folderFilter}
          options={displayedDirectories}
          loading={isLoadingDirs}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.path === value.path}
          filterOptions={items => items}
          onChange={(_event, option) => {
            if (option) {
              enterFolder(option.path);
            }
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setFolderFilter(value);
            }
          }}
          noOptionsText={atRoot ? 'No matching companies' : 'No subfolders here'}
          renderOption={(props, option) => (
            <li {...props} key={option.path}>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={navLabel}
              placeholder={atRoot ? 'Type a company name…' : 'Type to filter…'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingDirs ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {lockedIn ? (
        <>
          <Box>
            <Typography variant="subtitle2">Model Filename</Typography>
            <FileCheckboxList
              files={displayedModelFiles}
              selectedPath={selectedModelKey}
              onSelect={path => onSelectModel(toggleExclusivePath(selectedModelKey, path))}
              isLoading={isLoadingModel}
              emptyLabel="No matching files in the Model folder."
              idPrefix="workboard-model-file"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2">Final Report File Name</Typography>
            <FileCheckboxList
              files={displayedReportFiles}
              selectedPath={selectedReportKey}
              onSelect={path => onSelectReport(toggleExclusivePath(selectedReportKey, path))}
              isLoading={isLoadingReport}
              emptyLabel="No matching files in the Report folder."
              idPrefix="workboard-report-file"
            />
          </Box>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {atRoot
            ? 'Search for a company folder to begin.'
            : 'Select the valuation folder to choose the model and report files.'}
        </Typography>
      )}
    </Stack>
  );
};

export default WorkboardDropboxPicker;
